import { Button, Card, Container, Grid, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TuneIcon from '@mui/icons-material/Tune';
import { Box } from '@mui/system';
import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';

import Page from '../../components/Page';
import PageTitle from '../../components/PageHeading';
import BrudCrumbs from '../../components/BreadCrumbs';
import CustomInput from '../../components/CustomInput';
import CustomSelect from '../../components/CustomSelect';

import { saveApiData } from '../../store/Slice/Global/GlobalSlice';
import { UserApi } from '../../utils/Apis';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';
import { setApiSuccess } from '../../store/Slice/Error/ErrorSlice';

function EditIncentive() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { LoginData } = useLoginSelector();
  const { BankDrop, LoanTypeDrop } = useGlobalSelector();

  const incentiveData = location.state; // old incentive object

  // console.log('incentiveData2222', incentiveData);

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors }
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      incentiveType: '',
      minAmount: '',
      maxAmount: '',
      incentivePercent: '',
      companyPercentage: '',
      duration: '',
      bankId: '',
      loanType: ''
    }
  });

  /* ---------------- PREFILL DATA ---------------- */
  useEffect(() => {
    if (!incentiveData || !BankDrop || !LoanTypeDrop) return;

    // console.log('Prefilling form with incentiveData:', incentiveData);
    // console.log('Available LoanTypeDrop:', LoanTypeDrop);

    // Find matching bank from BankDrop (search by label if bankName is provided)
    const selectedBank =
      BankDrop?.find(
        (bank) => bank.value === incentiveData.bankId || bank.label === incentiveData.bankName
      ) || null;

    // Find matching loan type from LoanTypeDrop (search by LABEL since loanType comes as label)
    const selectedLoanType =
      LoanTypeDrop?.find((loan) => loan.label === incentiveData.loanType) || null;

    // Check if we found matches, if not, try exact match
    const finalLoanType =
      selectedLoanType ||
      (incentiveData.loanType
        ? { label: incentiveData.loanType, value: incentiveData.loanType }
        : null);

    const finalBank =
      selectedBank ||
      (incentiveData.bankName
        ? { label: incentiveData.bankName, value: incentiveData.bankId || incentiveData.bankName }
        : null);

    reset({
      incentiveType: incentiveData.incentiveType || '',
      minAmount: incentiveData.minAmount || '',
      maxAmount: incentiveData.maxAmount || '',
      incentivePercent: incentiveData.incentivePercent || '',
      companyPercentage: incentiveData.companyPercentage || '',
      duration: incentiveData.duration || '',
      bankId: finalBank || '',
      loanType: finalLoanType || ''
    });
  }, [incentiveData, reset, BankDrop, LoanTypeDrop]);

  /* ---------------- SUBMIT ---------------- */
  const onSubmit = useCallback(
    async (data) => {
      let payload = {
        id: incentiveData?.id,
        incentiveType: incentiveData?.incentiveType,
        incentivePercent: Number(data.incentivePercent),
        duration: data.duration,
        companyPercentage: Number(data.companyPercentage),
        userId: LoginData?.userId
      };

      // ✅ TARGET BASED
      if (incentiveData?.incentiveType === 'TARGET_BASED') {
        payload = {
          ...payload,
          minAmount: Number(data.minAmount),
          maxAmount: Number(data.maxAmount)
        };
      }

      // ✅ DISBURSEMENT BASED
      if (incentiveData?.incentiveType === 'DISBURSEMENT_BASED') {
        payload = {
          ...payload,
          bankId: data.bankId?.value ?? data.bankId,
          loanTypeId: data.loanType?.value ?? data.loanType
        };
      }

      const res = await dispatch(saveApiData(UserApi.editIncentive, payload));

      if (res?.status === 'success') {
        dispatch(setApiSuccess('Updated successfully'));
        navigate(-1);
      }
    },
    [dispatch, incentiveData, LoginData, navigate]
  );

  const newBankDrop = [{ label: 'All', value: 0 }, ...BankDrop] || [];
  const newMasterLoanTypeDrop = [{ label: 'All', value: 0 }, ...LoanTypeDrop] || [];

  return (
    <Page title="Kpros || Edit Incentive">
      <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: 8, pt: 2.5 }}>
        <Container maxWidth="xl">
          {/* Header */}
          <Box sx={{ mb: 3 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                color: '#64748b',
                fontWeight: 600,
                fontSize: '0.875rem',
                textTransform: 'none',
                mb: 1.5,
                p: 0,
                '&:hover': { color: '#0f172a', bgcolor: 'transparent' }
              }}
            >
              Back to Incentive Management
            </Button>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
              spacing={2}
            >
              <Box>
                <Typography
                  variant="h4"
                  sx={{
                    color: '#0f172a',
                    fontWeight: 800,
                    fontSize: { xs: '1.5rem', md: '1.75rem' },
                    letterSpacing: '-0.02em',
                    mb: 0.5
                  }}
                >
                  Edit Incentive
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  Update partner and employee incentive parameters
                </Typography>
              </Box>
              <Box>
                <BrudCrumbs />
              </Box>
            </Stack>
          </Box>

          <Card
            sx={{
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
              p: { xs: 2.5, md: 3 },
              bgcolor: '#ffffff'
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{ mb: 2.5, pb: 2, borderBottom: '1px solid #f1f5f9' }}
            >
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '8px',
                  bgcolor: '#0284c7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ffffff'
                }}
              >
                <TuneIcon sx={{ fontSize: 18 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>
                  Incentive Details
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                  {incentiveData?.incentiveType === 'TARGET_BASED'
                    ? 'Target based criteria and percentage allocation'
                    : 'Disbursement based configuration and partner share'}
                </Typography>
              </Box>
            </Stack>

            <form id="EditIncentiveForm" onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={3}>
                {incentiveData?.incentiveType === 'TARGET_BASED' && (
                  <>
                    <Grid item xs={12} sm={6} md={4}>
                      <CustomInput
                        name="minAmount"
                        label="Minimum Amount"
                        control={control}
                        errors={errors}
                        isRequired
                        fieldType="NUMBER"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <CustomInput
                        name="maxAmount"
                        label="Maximum Amount"
                        control={control}
                        errors={errors}
                        isRequired
                        fieldType="NUMBER"
                      />
                    </Grid>
                  </>
                )}

                {incentiveData?.incentiveType === 'DISBURSEMENT_BASED' && (
                  <>
                    <Grid item xs={12} sm={6} md={4}>
                      <CustomSelect
                        name="bankId"
                        label="Bank"
                        control={control}
                        options={newBankDrop}
                        errors={errors}
                        isRequired
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <CustomSelect
                        name="loanType"
                        label="Loan Type"
                        control={control}
                        options={newMasterLoanTypeDrop}
                        errors={errors}
                        isRequired
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm={6} md={4}>
                  <CustomInput
                    name="incentivePercent"
                    label="Incentive Percentage"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldType="DECIMAL"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <CustomInput
                    name="companyPercentage"
                    label="Company Percentage"
                    control={control}
                    errors={errors}
                    isRequired
                    fieldType="DECIMAL"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <CustomSelect
                    name="duration"
                    label="Duration"
                    control={control}
                    errors={errors}
                    isRequired
                    options={[
                      { label: 'Monthly', value: 'MONTHLY' },
                      { label: 'Quarterly', value: 'QUARTERLY' },
                      { label: 'Yearly', value: 'YEARLY' }
                    ]}
                  />
                </Grid>
              </Grid>

              <Box mt={3} textAlign="right">
                <Button
                  variant="contained"
                  type="submit"
                  form="EditIncentiveForm"
                  sx={{
                    bgcolor: '#0284c7',
                    color: '#ffffff',
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3.5,
                    py: 1,
                    boxShadow: '0 4px 12px rgba(2, 132, 199, 0.25)',
                    '&:hover': {
                      bgcolor: '#0369a1',
                      boxShadow: '0 6px 16px rgba(2, 132, 199, 0.35)'
                    }
                  }}
                >
                  Update Incentive
                </Button>
              </Box>
            </form>
          </Card>
        </Container>
      </Box>
    </Page>
  );
}

export default EditIncentive;
